import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  Mail, Send, Clock, CheckCircle, XCircle, Loader2, 
  BarChart3, ArrowRight, MessageSquare, AlertTriangle, Filter
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/utils';

const CommunicationDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);

  useEffect(() => {
    fetchAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    try {
      const [statsRes, logsRes] = await Promise.all([
        api.get('/communication-logs/stats?days=30'),
        api.get('/communication-logs/recent?days=30&limit=50')
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (logsRes.ok) {
        const data = await logsRes.json();
        setRecentLogs(data.logs || data.communications || []);
      }
    } catch (error) {
      console.error('Failed to fetch communication logs:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const TYPE_LABELS = {
    'candidate_rejection': 'Rejet candidat',
    'candidate_interview_invitation': 'Invitation entretien',
    'candidate_status_notification': 'Notification statut',
    'evaluation_form_sent': 'Formulaire évaluation',
    'meeting_invitation': 'Invitation meeting',
    'client_link': 'Lien client',
  };

  const STATUS_COLORS = {
    'sent': 'bg-green-100 text-green-700',
    'queued': 'bg-yellow-100 text-yellow-700',
    'failed': 'bg-red-100 text-red-700',
    'pending': 'bg-gray-100 text-gray-600',
  };

  const totalEmails = stats?.total_emails || 0;
  const byStatus = stats?.by_status || {};
  const byType = stats?.by_type || {};

  const sentCount = byStatus.sent || 0;
  const queuedCount = byStatus.queued || 0;
  const failedCount = byStatus.failed || 0;
  const successRate = totalEmails > 0 ? Math.round((sentCount / totalEmails) * 100) : 0;

  const filteredLogs = selectedType 
    ? recentLogs.filter(l => l.email_type === selectedType) 
    : recentLogs;

  return (
    <div className="p-4 md:p-8" data-testid="communication-dashboard">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-display font-semibold text-tp-text">Communications</h1>
        <p className="text-tp-text-light text-sm">Historique des emails et notifications (30 derniers jours)</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-6">
        <Card className="p-3 md:p-5 border-tp-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-tp-primary/10">
              <Mail className="h-5 w-5 text-tp-primary" />
            </div>
            <div>
              <p className="text-xs text-tp-text-light">Total envoyés</p>
              <p className="text-xl font-display font-bold text-tp-text">{totalEmails}</p>
            </div>
          </div>
        </Card>

        <Card className="p-3 md:p-5 border-tp-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-tp-text-light">Envoyés</p>
              <p className="text-xl font-display font-bold text-green-600">{sentCount}</p>
            </div>
          </div>
        </Card>

        <Card className="p-3 md:p-5 border-tp-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-100">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-xs text-tp-text-light">En file</p>
              <p className="text-xl font-display font-bold text-yellow-600">{queuedCount}</p>
            </div>
          </div>
        </Card>

        <Card className="p-3 md:p-5 border-tp-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-100">
              <XCircle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-tp-text-light">Échoués</p>
              <p className="text-xl font-display font-bold text-red-600">{failedCount}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Success Rate + Types Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
        <Card className="p-4 md:p-6 border-tp-border">
          <h3 className="text-base font-display font-semibold text-tp-text mb-4 flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-tp-accent" />
            Taux de réussite
          </h3>
          <div className="text-center mb-4">
            <span className="text-4xl font-display font-bold text-tp-primary">{successRate}%</span>
          </div>
          <Progress value={successRate} className="h-3" />
          <p className="text-xs text-tp-text-light mt-2 text-center">
            {sentCount} envoyés sur {totalEmails} total
          </p>
        </Card>

        <Card className="p-4 md:p-6 border-tp-border">
          <h3 className="text-base font-display font-semibold text-tp-text mb-4 flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-tp-accent" />
            Par type
          </h3>
          <div className="space-y-2">
            {Object.entries(byType).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <button
                  onClick={() => setSelectedType(selectedType === type ? null : type)}
                  className={`text-sm ${selectedType === type ? 'font-semibold text-tp-primary' : 'text-tp-text-light hover:text-tp-text'}`}
                >
                  {TYPE_LABELS[type] || type}
                </button>
                <Badge variant="secondary">{count}</Badge>
              </div>
            ))}
            {Object.keys(byType).length === 0 && (
              <p className="text-sm text-tp-text-light text-center py-4">Aucune donnée</p>
            )}
          </div>
        </Card>
      </div>

      {/* Recent Logs */}
      <Card className="p-4 md:p-6 border-tp-border" data-testid="communication-logs-list">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-display font-semibold text-tp-text flex items-center gap-2">
            <Send className="h-4 w-4 text-tp-accent" />
            Historique récent
          </h3>
          {selectedType && (
            <Button variant="ghost" size="sm" onClick={() => setSelectedType(null)} className="text-xs">
              <Filter className="h-3 w-3 mr-1" />
              Effacer filtre
            </Button>
          )}
        </div>

        <div className="space-y-2 max-h-[500px] overflow-y-auto">
          {filteredLogs.length > 0 ? (
            filteredLogs.map((log, idx) => (
              <div key={log.id || idx} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50">
                <div className={`p-1.5 rounded-lg flex-shrink-0 ${STATUS_COLORS[log.status] || 'bg-gray-100 text-gray-600'}`}>
                  {log.status === 'sent' ? <CheckCircle className="h-4 w-4" /> :
                   log.status === 'failed' ? <XCircle className="h-4 w-4" /> :
                   log.status === 'queued' ? <Clock className="h-4 w-4" /> :
                   <Mail className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {log.subject || TYPE_LABELS[log.email_type] || log.email_type}
                    </span>
                    <Badge variant="outline" className="text-[10px] flex-shrink-0">
                      {TYPE_LABELS[log.email_type] || log.email_type}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    {log.recipient_name || log.recipient_email || 'N/A'}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">{formatDate(log.created_at)}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Mail className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Aucune communication récente</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CommunicationDashboardPage;
